import React, { useState, useEffect } from "react";
import Sidebar from "../../components/Sidebar";
import TopBar from "../../components/TopBar";
import dayjs from "dayjs";
import "dayjs/locale/zh-cn";
import { theme, Badge, Button, DatePicker, Select, message, Pagination, Table } from "antd";
import moment from "moment";
import dayLocaleData from "dayjs/plugin/localeData";
import { IoFilterOutline } from "react-icons/io5";
import {
  CalendarDays,
  Globe,
  Lock,
  MapPin,
  Video,
  LayoutGrid,
  List,
  ClipboardList,
  Monitor,
  Building2,
} from "lucide-react";
import { useDispatch } from "react-redux";
import { getWellness } from "../../api/Wellness/WellnessSlice";
import total_session from "../../../../assets/images/total_session.png";
import total_virtual from "../../../../assets/images/total_virtual.png";
import total_onsite from "../../../../assets/images/total_onsite.png";
import total_survey from "../../../../assets/images/total_survey.png";
import CalenderCounter from "./CalenderCounter";
import CreateEventModal from "../../components/CreateEventModal";
import "./index.css";
dayjs.extend(dayLocaleData);
export default function Index() {
  const { token } = theme.useToken();
  const dispatch = useDispatch();
  const [date, setDate] = useState(moment().format("DD-MM-YYYY"));
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totalSession, setTotalSession] = useState("00");
  const [totalVirtual, setTotalVirtual] = useState("00");
  const [totalOnCampus, setTotalOnCampus] = useState("00");
  const [totalSurvey, setTotalSurvey] = useState("00");
  const [showFilter, setShowFilter] = useState(false);
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [type, setType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);
  const [collapsed, setCollapsed] = useState(false);
  const [activeView, setActiveView] = useState("grid"); // "grid" | "table"
  const [showCreateEvent, setShowCreateEvent] = useState(false);

  const switchView = (view) => {
    setActiveView(view);
    setCurrentPage(1);
    setPageSize(view === "grid" ? 9 : 10);
  };

  const viewClass = (isActive) =>
    `flex items-center justify-center gap-1.5 px-[12px] h-[30px] rounded-lg border-0 text-[12px] font-semibold font-poppins transition-all ${
      isActive
        ? "bg-[#0F172A] text-white shadow-md"
        : "bg-[#F8FAFC] text-[#334155] hover:bg-gray-100"
    }`;

  // ---- Table view helpers ----
  const getType = (item) => {
    if (item.type === "SURVEY") {
      return "Survey";
    } else if (item.eventType === "ON_CAMPUS") {
      return "On Campus";
    } else {
      return "Virtual";
    }
  };

  const getTypeIcon = (item) => {
    if (item.type === "SURVEY") {
      return { Icon: ClipboardList, tone: "bg-[#F5F3FF] text-[#7C3AED]" };
    } else if (item.eventType === "VIRTUAL") {
      return { Icon: Monitor, tone: "bg-[#F0FDFA] text-[#0D9488]" };
    }
    return { Icon: Building2, tone: "bg-[#FFF7ED] text-[#EA580C]" };
  };

  // Same start-date comparison the table always used (today / ended / scheduled)
  const getStatusPill = (start) => {
    const dateToCheck = moment(start);
    const currentDate = moment();
    if (dateToCheck.isSame(currentDate, "day")) {
      return { label: "Today", dot: "bg-[#10B981]", cls: "bg-[#ECFDF5] text-[#059669]" };
    } else if (dateToCheck.isBefore(currentDate)) {
      return { label: "Ended", dot: "bg-[#DC2626]", cls: "bg-[#FEF2F2] text-[#DC2626]" };
    }
    return { label: "Scheduled", dot: "bg-[#F59E0B]", cls: "bg-[#FFFBEB] text-[#B45309]" };
  };

  const getDuration = (start, end) => {
    const duration = moment.duration(moment(end).diff(moment(start)));
    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();

    let result = `${days} day${days !== 1 ? "s" : ""}`;
    if (hours > 0) result += ` ${hours} hour${hours !== 1 ? "s" : ""}`;
    if (minutes > 0) result += ` ${minutes} minute${minutes !== 1 ? "s" : ""}`;
    return result;
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, record, index) => (
        <span className="font-poppins">{(currentPage - 1) * pageSize + index + 1}</span>
      ),
    },
    {
      title: "TYPE",
      key: "type",
      render: (_, record) => {
        const { Icon, tone } = getTypeIcon(record);
        return (
          <div className="flex items-center gap-2">
            <div className={`flex items-center justify-center w-[34px] h-[34px] rounded-xl flex-shrink-0 ${tone}`}>
              <Icon size={16} />
            </div>
            <span className="font-semibold text-[#171717] font-poppins">{getType(record)}</span>
          </div>
        );
      },
    },
    {
      title: "TITLE",
      key: "title",
      dataIndex: "title",
      render: (text) => <span className="font-poppins">{text}</span>,
    },
    {
      title: "LOCATION",
      key: "location",
      render: (_, record) =>
        record.eventType === "VIRTUAL" ? (
          <a
            href={getMeetingLisk(record.meetingLink)}
            target={"_blank"}
            rel="noreferrer"
            title={record.meetingLink}
            className="font-poppins font-semibold text-[#2563EB] hover:underline"
          >
            Link
          </a>
        ) : (
          <span className="font-poppins">{record.venue}</span>
        ),
    },
    {
      title: "START DATE",
      key: "start_date",
      render: (_, record) => (
        <span className="font-poppins">{moment(record.startTime).format("DD MMM YY")}</span>
      ),
    },
    {
      title: "DURATION",
      key: "duration",
      render: (_, record) => (
        <span className="font-poppins">{getDuration(record.startTime, record.endTime)}</span>
      ),
    },
    {
      title: "STATUS",
      key: "status",
      render: (_, record) => {
        const pill = getStatusPill(record.startTime);
        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold font-poppins ${pill.cls}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${pill.dot}`} />
            {pill.label}
          </span>
        );
      },
    },
  ];

  // Event card — mirrors user-web's CompanyEventsTab card
  const TYPE_LABEL = {
    WELLNESS_SESSION: "Wellness Session",
    HEALTH_CAMPUS: "Health Camp",
    SURVEY: "Survey",
  };

  const EventCard = ({ item }) => {
    const isVirtual = item.eventType === "VIRTUAL";
    const isPublic = item.visibility === "PUBLIC";
    const location = item.type === "SURVEY" || isVirtual ? item.meetingLink : item.venue;
    const isPast = new Date(item.endTime) < new Date();
    return (
      <div className="flex flex-col h-full bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl p-6 hover:shadow-xl hover:border-[#4B61FF]/30 transition-all font-poppins min-w-0">
        {/* Type + visibility */}
        <div className="flex justify-between items-start mb-4">
          <div className="px-3 py-1 rounded-full text-xs font-bold border-[1px] border-solid bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/30">
            {TYPE_LABEL[item.type] || item.type}
          </div>
          {item.visibility && (
            <span
              className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${
                isPublic ? "text-[#10B981]" : "text-[#64748B]"
              }`}
            >
              {isPublic ? <Globe size={12} /> : <Lock size={12} />}
              {item.visibility}
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="text-lg font-bold mb-2 text-[#171717]">{item.title}</h3>

        {/* Details */}
        <div className="space-y-2 mb-6 p-3 rounded-xl flex-1 bg-[#F1F5F9]">
          <div className="flex items-center gap-2 text-xs text-[#334155]">
            <CalendarDays size={14} className="text-[#64748B] flex-shrink-0" />
            {moment(item.startTime).format("DD MMM YYYY")} • {moment(item.startTime).format("hh:mm A")}
          </div>
          <div className="flex items-center gap-2 text-xs text-[#334155] min-w-0">
            {isVirtual ? (
              <Video size={14} className="text-[#3B82F6] flex-shrink-0" />
            ) : (
              <MapPin size={14} className="text-[#DC2626] flex-shrink-0" />
            )}
            {isVirtual && location ? (
              <a
                href={getMeetingLisk(location)}
                target={"_blank"}
                rel="noreferrer"
                title={location}
                className="truncate text-[#334155] no-underline hover:text-[#2563EB] hover:underline"
              >
                {location}
              </a>
            ) : (
              <span className="truncate" title={location}>{location || "—"}</span>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center pt-4 mt-auto border-t-[1px] border-solid border-x-0 border-b-0 border-[#E2E8F0]">
          <span
            className={`text-xs font-bold px-2 py-1 rounded border-[1px] border-solid ${
              isPast
                ? "text-[#64748B] bg-[#F1F5F9] border-[#E2E8F0]"
                : "text-[#10B981] bg-[#10B981]/10 border-[#10B981]/30"
            }`}
          >
            {isPast ? "Past" : "Upcoming"}
          </span>
        </div>
      </div>
    );
  };
  const onPanelChange = (value, mode) => {
    try {
      console.log(value.format("YYYY-MM-DD"));
      setDate(value.format("YYYY-MM-DD"));
      setFilteredData(filterEventsByDate(data, value.format("DD-MM-YYYY")));
      console.log(data);
      console.log("filtered====>", filteredData);
    } catch (error) {
      console.log("error====>", error);
    }
  };

  const getMeetingLisk = (url) => {
    if (url?.startsWith("https://")) {
      return url;
    } else if (url?.startsWith("http://")) {
      return url;
    } else {
      return "https://" + url;
    }
  };
  const getData = () => {
    setLoading(true);
    dispatch(getWellness()).then((res) => {
      if (Array.isArray(res.payload.message.data.payload.data)) {
        setData(res.payload.message.data.payload.data);
        setFilteredData(res.payload.message.data.payload.data);
        getCount();
      }
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  };
  const getCount = () => {
    setTotalSession(data.length);
    let finalArr = filteredData;
    let virtual = finalArr.filter((item) => item.eventType === "VIRTUAL");
    let onCampus = finalArr.filter((item) => item.eventType === "ON_CAMPUS");
    let survey = finalArr.filter((item) => item.type === "SURVEY");
    setTotalVirtual(virtual.length);
    setTotalOnCampus(onCampus.length);
    setTotalSurvey(survey.length);
  };
  useEffect(() => {
    getData();
  }, []);
  useEffect(() => {
    getCount();
  }, [data, filteredData]);

  const filterEventsByDate = (events, date) => {
    console.log("date===>", date);
    return events.filter((event) => {
      return moment(event.startTime).isSame(moment(date, "DD-MM-YYYY"), "day");
    });
  };
  const getEventsForDate = (date) => {
    return data.filter((event) =>
      moment(event.startTime).isSame(date.format("DD-MM-YYYY"), "day")
    );
  };

  const dateCellRender = (value) => {
    const eventsForDay = filterEventsByDate(data, value.format("DD-MM-YYYY"));
    console.log("hereday====>", value.format("DD-MM-YYYY"), eventsForDay);
    if (eventsForDay.length > 0) {
      return (
        <div className="flex justify-center">
          <Badge
            count={eventsForDay.length}
            style={{ backgroundColor: "#2563EB" }}
          />
        </div>
      );
    }

    return null;
  };

  const doFilter = () => {
    setLoading(true);
    setTimeout(() => {
      if (start !== null && end !== null) {
        const from = moment(start, "YYYY-MM-DD").startOf("day");
        const to = moment(end, "YYYY-MM-DD").endOf("day");
        console.log(from, to);
        if (type === "All") {
          let arr = data.filter((item) => {
            const startTime = moment(item.startTime);
            const endTime = moment(item.endTime);
            return (
              startTime.isBetween(from, to, null, "[]") ||
              endTime.isBetween(from, to, null, "[]")
            );
          });
          setFilteredData(arr);
        } else if (type === "VIRTUAL") {
          let arr = data.filter((item) => {
            const startTime = moment(item.startTime);
            const endTime = moment(item.endTime);
            return (
              (item.eventType === type &&
                startTime.isBetween(from, to, null, "[]")) ||
              endTime.isBetween(from, to, null, "[]")
            );
          });

          let arr1 = arr.filter(
            (item) =>
              item.type === "WELLNESS_SESSION" && item.type === "HEALTH_CAMPUS"
          );

          console.log("arr1=======>", arr1);
          setFilteredData(arr);
        } else if (type === "ON_CAMPUS") {
          let arr = data.filter((item) => {
            const startTime = moment(item.startTime);
            const endTime = moment(item.endTime);
            return (
              (item.eventType === type &&
                startTime.isBetween(from, to, null, "[]")) ||
              endTime.isBetween(from, to, null, "[]")
            );
          });
          setFilteredData(arr);
        } else if (type === "SURVEY") {
          let arr = data.filter((item) => {
            const startTime = moment(item.startTime);
            const endTime = moment(item.endTime);
            return (
              (item.type === type && startTime.isBetween(from, to, null, "[]")) ||
              endTime.isBetween(from, to, null, "[]")
            );
          });
          setFilteredData(arr);
        }
      } else {
        if (type === "All") {
          setFilteredData(data);
        } else if (type === "VIRTUAL") {
          let arr = data.filter((item) => {
            return item.eventType === type;
          });
          let arr1 = arr.filter((item) => item.type !== "SURVEY");
          setFilteredData(arr1);
        } else if (type === "ON_CAMPUS") {
          let arr = data.filter((item) => {
            return item.eventType === type;
          });
          setFilteredData(arr);
        } else if (type === "SURVEY") {
          let arr = data.filter((item) => {
            return item.type === type;
          });
          setFilteredData(arr);
        }
      }
      setLoading(false);
    }, 300);
  };

  console.log(filteredData);

  return (
    <div className="fixed inset-x-0 bottom-0 top-16 flex bg-[#F8FAFC] overflow-hidden">
      <TopBar />

      {/* Sidebar */}
      <div className={`hidden lg:flex ${collapsed ? "w-[64px]" : "w-[272px]"} fixed top-0 left-0 h-screen z-20`}>
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>

      {/* Main Content */}
      <div className={`flex-1 min-w-0 overflow-auto bg-[#F8FAFC] pt-2 px-2 pb-8 transition-all duration-300 ${collapsed ? "lg:ml-[64px]" : "lg:ml-[272px]"}`}>
        <div className="p-4 md:p-6 bg-[#FFFFFF] min-h-screen rounded-2xl">
          <div className="w-full bg-white">

            {/* Header with buttons */}
            <div className="relative flex justify-between items-center p-2 mb-3">
              <h1 className="text-xl font-bold text-[#171717] font-poppins mb-0">
                Events Calendar
              </h1>
              <div className="flex gap-2">
                <Button
                  type="primary"
                  onClick={() => setShowCreateEvent(true)}
                  className="btn-gradient border-0 font-poppins font-semibold text-[13px] h-9 px-[14px] rounded-xl"
                >
                  Schedule A New Session
                </Button>
                <Button
                  className="flex items-center gap-2 h-9 px-[14px] bg-white text-[#171717] text-[13px] font-semibold font-poppins border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-[#F8FAFC] transition"
                  icon={<IoFilterOutline size={16} color={"#2563EB"} />}
                  onClick={() => setShowFilter(!showFilter)}
                >
                  Filter By
                </Button>
              </div>

              {/* Filter Box - positioned relative to header */}
              {showFilter && (
                <div className="absolute top-full right-0 mt-2 w-80 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl shadow-lg p-6 z-50 font-poppins">
                  <div className="space-y-4">
                    <div>
                      <span className="block text-sm font-medium text-[#334155] mb-2">Date Range</span>
                      <div className="flex gap-2">
                        <DatePicker
                          onChange={(date, dateString) => setStart(dateString)}
                          placeholder="From"
                          className="flex-1 h-9 rounded-xl border-[#E2E8F0] shadow-sm"
                        />
                        <DatePicker
                          onChange={(date, dateString) => setEnd(dateString)}
                          placeholder="To"
                          className="flex-1 h-9 rounded-xl border-[#E2E8F0] shadow-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <span className="block text-sm font-medium text-[#334155] mb-2">Event Type</span>
                      <Select
                        options={[
                          { label: "All", value: "All" },
                          { label: "Virtual", value: "VIRTUAL" },
                          { label: "On-Premise", value: "ON_CAMPUS" },
                          { label: "Survey", value: "SURVEY" },
                        ]}
                        defaultValue={"All"}
                        className="w-full"
                        onChange={(v) => setType(v)}
                      />
                    </div>

                    <div className="flex justify-end">
                      <Button
                        className="btn-gradient font-poppins font-semibold text-[13px] h-9 px-[14px] rounded-xl border-0"
                        onClick={() => {
                          doFilter();
                          setShowFilter(false);
                        }}
                      >
                        Apply Filters
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Counter Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2.5 p-2 mb-3">
              <CalenderCounter
                icon={total_session}
                title={"Total Sessions"}
                count={totalSession}
                fontSize={"16px"}
              />
              <CalenderCounter
                icon={total_virtual}
                title={"Virtual"}
                count={totalVirtual}
              />
              <CalenderCounter
                icon={total_onsite}
                title={"On-Premise"}
                count={totalOnCampus}
              />
              <CalenderCounter
                icon={total_survey}
                title={"Survey"}
                count={totalSurvey}
              />
            </div>

            {/* Events Section */}
            <div className="p-2">
              {/* View switcher */}
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold text-[#171717] font-poppins mb-0">
                  All Events
                </h2>
                <div className="inline-flex items-center gap-1 p-1 bg-white rounded-xl border-[1px] border-solid border-[#E2E8F0] shadow-sm">
                  <button
                    type="button"
                    onClick={() => switchView("grid")}
                    className={viewClass(activeView === "grid")}
                    title="Card view"
                  >
                    <LayoutGrid size={14} />
                    Cards
                  </button>
                  <button
                    type="button"
                    onClick={() => switchView("table")}
                    className={viewClass(activeView === "table")}
                    title="Table view"
                  >
                    <List size={14} />
                    Table
                  </button>
                </div>
              </div>

              {loading && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563EB] mx-auto"></div>
                  <p className="mt-2 text-[#64748B] font-poppins">Loading events...</p>
                </div>
              )}

              {!loading && activeView === "grid" && filteredData.length === 0 && (
                <div className="text-center py-12 text-[#64748B] text-sm font-poppins">
                  No events found
                </div>
              )}

              {!loading && activeView === "table" && (
                <Table
                  columns={columns}
                  dataSource={filteredData.map((item, index) => ({
                    ...item,
                    key: item.id || item.wellnessId || index,
                  }))}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: filteredData.length,
                    showSizeChanger: false,
                    showQuickJumper: false,
                    className: "font-poppins",
                    position: ["bottomCenter"],
                    onChange: (page, size) => {
                      setCurrentPage(page);
                      setPageSize(size);
                    },
                  }}
                  bordered={false}
                  className="calendar-table"
                  rowClassName={() => "bg-white text-[#334155] text-[13px] hover:bg-[#F8FAFC] transition"}
                  scroll={{ x: "max-content" }}
                />
              )}

              {!loading && activeView === "grid" && filteredData.length > 0 && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredData
                      .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                      .map((item, index) => (
                        <EventCard key={item.id || item.wellnessId || index} item={item} />
                      ))}
                  </div>
                  <div className="flex justify-center mt-6">
                    <Pagination
                      current={currentPage}
                      pageSize={pageSize}
                      total={filteredData.length}
                      showSizeChanger={false}
                      className="font-poppins"
                      onChange={(page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <CreateEventModal
        isOpen={showCreateEvent}
        onClose={() => setShowCreateEvent(false)}
        onSuccess={getData}
      />
    </div>
  );
}
